import React, { useState } from 'react';
import { X, AlertTriangle, Clock, Zap } from 'lucide-react';
import { User } from '../types';
import { authService } from '../services/authService';

interface TrialBannerProps {
  user: User;
}

function fmtCredits(n: number): string {
  return Math.abs(n) >= 1000
    ? n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : n.toFixed(2);
}

const TrialBanner: React.FC<TrialBannerProps> = ({ user }) => {
  const [dismissedPayg, setDismissedPayg] = useState(false);
  const [dismissedTrial, setDismissedTrial] = useState(false);

  const isAdmin = authService.hasPermission(user, 'ADMIN');

  const licensingLink = isAdmin ? (
    <a
      href="/settings/licensing"
      className="px-3 py-1 rounded bg-white text-red-700 font-semibold text-xs hover:bg-red-50 transition-colors"
    >
      Go to Licensing →
    </a>
  ) : (
    <span className="text-xs opacity-80">Contact your admin to purchase credit.</span>
  );

  const hasCreditsData = user.credits_balance != null;
  const isPaygMode = hasCreditsData && (user.credits_balance as number) <= 0;

  // Credits depleted / PAYG warning — shown for any user regardless of license type
  if (!dismissedPayg && isPaygMode) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-600 text-white text-sm">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 flex-shrink-0" />
          <span>AI Credits depleted — usage is now billed at pay-as-you-go rates.</span>
        </div>
        <button
          onClick={() => setDismissedPayg(true)}
          className="p-1 rounded hover:bg-red-700 transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Trial banner (existing logic + credit progress)
  const isTrial = user.subscription_status === 'TRIAL';
  const isTrialExpiredStatus = user.subscription_status === 'EXPIRED';
  if (!dismissedTrial && (isTrial || isTrialExpiredStatus)) {
    const now = new Date();
    const expiresAt = user.billing_period_end ? new Date(user.billing_period_end) : null;
    const isExpired = isTrialExpiredStatus || (expiresAt ? now > expiresAt : true);
    const daysRemaining = expiresAt
      ? Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      : null;

    const creditsUsed =
      hasCreditsData && user.credits_included != null
        ? (user.credits_included as number) - (user.credits_balance as number)
        : null;
    const creditsLabel =
      creditsUsed != null && user.credits_included != null
        ? ` · ${fmtCredits(creditsUsed)} / ${fmtCredits(user.credits_included as number)} credits used`
        : '';

    if (isExpired || (daysRemaining !== null && daysRemaining <= 0)) {
      // Not dismissible — this reflects an active block on the account
      // (backend rejects scans/writes with 402 while the trial is expired),
      // so the banner needs to stay visible until credit is purchased.
      return (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-600 text-white text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>Free Trial expired — you cannot perform any operation until credit is purchased.</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">{licensingLink}</div>
        </div>
      );
    }

    // Trial-ending warning: admin-only, only within 3 days of expiry (matches
    // the backend's LICENSE_EXPIRING notification window).
    if (isAdmin && daysRemaining !== null && daysRemaining <= 3) {
      return (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500 text-white text-sm">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>
              {daysRemaining === 1
                ? `Your free trial expires tomorrow.${creditsLabel}`
                : `Your free trial expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.${creditsLabel}`}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href="/settings/licensing"
              className="px-3 py-1 rounded bg-white text-amber-700 font-semibold text-xs hover:bg-amber-50 transition-colors"
            >
              Upgrade
            </a>
            <button
              onClick={() => setDismissedTrial(true)}
              className="p-1 rounded hover:bg-amber-600 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }
  }

  return null;
};

export default TrialBanner;
